/**
 * Geometry for the coins/gems that fly from the revealed reward up to the
 * balance pills (ported from referral's reward reveal). Pure so the burst can
 * be planned and tested without a DOM; the reveal measures the real rects.
 */

export interface TargetRect {
  left: number
  top: number
  width: number
  height: number
}

export interface Flyer {
  id: number
  kind: 'coin' | 'gem'
  startX: number
  startY: number
  endX: number
  endY: number
  /** Stagger so the burst leaves in a trickle rather than all at once. */
  delayMs: number
}

export interface PlanFlyersOptions {
  /** Where the reward sits — the centre the coins launch from. */
  source: { x: number; y: number }
  coinTarget?: TargetRect
  gemTarget?: TargetRect
  /** The delivered amounts; a currency only flies when it was granted. */
  delta: { coin: number; gem: number }
  perCurrency?: number
}

const DEFAULT_PER_CURRENCY = 6
const STAGGER_MS = 70
// A small fixed spread so a burst reads as several coins, not one — index
// -driven (not random) to keep planning deterministic and testable.
const JITTER = [
  { x: 0, y: 0 },
  { x: -14, y: 6 },
  { x: 14, y: 6 },
  { x: -8, y: -10 },
  { x: 8, y: -10 },
  { x: 0, y: 12 },
]

function centre(rect: TargetRect): { x: number; y: number } {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}

export function planFlyers(options: PlanFlyersOptions): Flyer[] {
  const { source, delta } = options
  const perCurrency = options.perCurrency ?? DEFAULT_PER_CURRENCY
  const flyers: Flyer[] = []
  let id = 0

  const burst = (kind: 'coin' | 'gem', target: TargetRect | undefined) => {
    if (target == null) return
    const end = centre(target)
    for (let i = 0; i < perCurrency; i++) {
      const jitter = JITTER[i % JITTER.length]
      flyers.push({
        id: id++,
        kind,
        startX: source.x + jitter.x,
        startY: source.y + jitter.y,
        endX: end.x,
        endY: end.y,
        delayMs: i * STAGGER_MS,
      })
    }
  }

  if (delta.coin > 0) burst('coin', options.coinTarget)
  if (delta.gem > 0) burst('gem', options.gemTarget)
  return flyers
}
